/** @format */

import React, {useEffect, useRef} from 'react';
import SuperAgent from 'superagent';
import InputWithErrorHandler from './input';
import './address_autocomplete.sass';
import {useFetch} from 'frontier-utilities';

const useAddressAutocomplete = ({inputValue = ''}) => {
    const [predictions, setPredictions] = React.useState([]);
    const [placeDetail, setPlaceDetail] = React.useState({});

    const {fetchData: fetchPredictions} = useFetch(
        '/account/addresses/place-autocomplete',
        ({predictions}) => setPredictions(predictions),
        50
    );

    const {fetchData: fetchPlaceDetail, isLoading: isLoadingPlaceDetail} = useFetch(
        '/account/addresses/place-detail',
        setPlaceDetail,
        50
    );

    React.useEffect(() => {
        if (inputValue.length > 0) {
            fetchPredictions({
                method: 'POST',
                body: JSON.stringify({input: inputValue})
            });
        }
    }, [inputValue]);

    return {
        predictions,
        setPredictions,
        placeDetail,
        fetchPlaceDetail,
        isLoadingPlaceDetail
    };
};

function getHighlightedText(description, matched) {
    const highlighted = [];
    let lastIndex = 0;
    matched.forEach((string, index) => {
        if (lastIndex !== string.offset && lastIndex < string.offset) {
            highlighted.push(
                <span key={index + '_1'}>{description.slice(lastIndex, string.offset)}</span>
            );
        }

        highlighted.push(
            <strong key={index + '_2'}>
                {description.slice(string.offset, string.offset + string.length)}
            </strong>
        );

        lastIndex = string.offset + string.length;
        if (index + 1 === matched.length) {
            highlighted.push(<span key={index + '_3'}>{description.slice(lastIndex)}</span>);
        }
    });

    return highlighted.map(m => m);
}

function PredictionItem({item, isCurrent, ...props}) {
    const className = ['address_autocomplete__item'];
    if (isCurrent) {
        className.push('address_autocomplete__highlighted');
    }

    return (
        <div className={className.join(' ')} {...props}>
            {getHighlightedText(item.description, item.matched_substrings)}
        </div>
    );
}

const AddressAutocompleteInput = ({
    predictions,
    value,
    isLoadingPlaceDetail = false,
    onSelect = () => {},
    renderInputComponent = props => {
        return <InputWithErrorHandler {...props} />;
    },
    renderPredictionItem = props => {
        return <PredictionItem {...props} />;
    },
    ...inputProps
}) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [isShowingPredictions, setIsShowingPredictions] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(value ?? '');
    const [isFocused, setIsFocused] = React.useState(false);
    const predictionCountRef = React.useRef();
    const inputRef = React.useRef();

    const handleKeyPress = ({key}) => {
        switch (key) {
            case 'Enter':
                setIsShowingPredictions(false);
                setInputValue(predictions[selectedIndex].description);
                onSelect(predictions[selectedIndex].place_id);
                setSelectedIndex(0);
                break;
            case 'ArrowUp':
                setSelectedIndex(Math.max(0, selectedIndex - 1));
                break;
            case 'ArrowDown':
                setSelectedIndex(Math.min(predictions.length - 1, selectedIndex + 1));
                break;
        }
    };

    const handleSelect = placeId => {
        setInputValue(predictions.find(p => placeId === p.place_id).description);
        setIsShowingPredictions(false);
        onSelect(placeId);
        setSelectedIndex(0);
    };

    const handleBlur = () => {
        setSelectedIndex(0);
        setIsShowingPredictions(false);
    };

    const handleFocus = () => {
        if (predictions.length > 1 || predictions[0]?.description !== inputValue) {
            setIsShowingPredictions(true);
        }
    };

    useEffect(() => {
        if (inputRef.current === document.activeElement) {
            setIsShowingPredictions(true);
        }
    }, [predictions]);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    if (isLoadingPlaceDetail) {
        inputProps.disabled = true;
    }

    return (
        <>
            {renderInputComponent({
                ...inputProps,
                inputRef,
                value: inputValue,
                onKeyDown: handleKeyPress,
                onBlur: handleBlur,
                onFocus: handleFocus
            })}
            {isShowingPredictions && (
                <div className="address_autocomplete__container">
                    {predictions.length > 0 && (
                        <div className="address_autocomplete__results">
                            {predictions.map((item, predictionIndex) => {
                                return renderPredictionItem({
                                    key: item.place_id,
                                    item,
                                    isCurrent: selectedIndex === predictionIndex,
                                    onMouseDown: handleSelect.bind(this, item.place_id),
                                    onMouseOver: setSelectedIndex.bind(this, predictionIndex)
                                });
                            })}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export {useAddressAutocomplete, AddressAutocompleteInput};

export default function ({onSelect, ...props}) {
    const {predictions, setPredictions, placeDetail, fetchPlaceDetail, isLoadingPlaceDetail} =
        useAddressAutocomplete({
            inputValue: props.value
        });

    const handleSelect = async placeId => {
        await fetchPlaceDetail({
            method: 'POST',
            body: JSON.stringify({place_id: placeId})
        });
    };

    useEffect(() => {
        if (placeDetail.formatted_address) {
            onSelect(placeDetail);
        }
    }, [placeDetail]);

    // rendering inputProps will cause an error in the console
    const {inputProps, ...rest} = props;
    return (
        <AddressAutocompleteInput
            {...rest}
            predictions={predictions}
            onSelect={handleSelect}
            isLoadingPlaceDetail={isLoadingPlaceDetail}
        />
    );
}
